import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SuperUserHomePageUI from '../../../templates/SuperUserHomePageUI';
import SpinnerComponentUI from '../../../components/common/SpinnerComponentUI';
import LRTableActionsUI from '../../../widgets/layout/LRTableActionsUI';
import IconButtonUI from '../../../widgets/action/IconButtonUI';
import CardUI from '../../../widgets/layout/CardUI';
import MonitoringIntelligencePageNavUI from './MonitoringIntelligencePageNavUI';
import './MISummary.css';
import MIDownloadButtonUI from './MIDownloadButtonUI';
import MIReportApproval from './MIReportApproval';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (num) =>
  Number(num || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parseAmt = (v) => parseFloat(String(v || '0').replace(/,/g, '')) || 0;

const calcDPD = (dateStr) => {
  if (!dateStr || dateStr === '-' || dateStr === 'N/A') return '-';
  try {
    const parsed = new Date(dateStr);
    if (isNaN(parsed.getTime())) return '-';
    const diff = Math.floor((new Date() - parsed) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  } catch (e) {
    return '-';
  }
};

// DSRA shortfall has no defaultDate (see the raw item dump — defaultDate is
// always null for reportType dsraShortfall). Its DPD clock instead starts
// from the account's last payment date.
const getDpdDateForItem = (item) => {
  if (!item) return null;
  if (item.reportType === 'dsraShortfall') return item.lastPaymentDate || null;
  return item.defaultDate || null;
};

const LEGAL_MONITORING_ACTIONS = ['filing of criminal complaint', 'court hearing'];
const HIDDEN_COMPLIANCE_CODES = new Set([
  'dsra_dip_in_case_of_levying_of_penal_charges',
  'dsra_dip_in_case_of_emi_default',
  'emi_default',
  'continuous_dsra_dip',
]);

const isLegalAccount = (rows) =>
  rows.some((row) => {
    const action = (
      row.item && row.item.monitoringAction ? row.item.monitoringAction : ''
    ).toLowerCase().trim();
    return LEGAL_MONITORING_ACTIONS.some((k) => action.includes(k));
  });

// A non-financial compliance item only counts as an actual OPEN
// non-compliance (and can therefore push an account into the Legal card,
// or appear anywhere on this page) if fileStatus says it's still open —
// this is the exact same rule the MI dashboard uses. We deliberately do
// NOT fall back to `status`, because `status` can stay 'not_complied'
// even after the document has been uploaded/approved (fileStatus moves
// to 'complied') — trusting `status` there is what caused already-
// complied items to keep showing up on this page.
const isOpenNonCompliance = (c) =>
  c.fileStatus === 'not_complied' &&
  !HIDDEN_COMPLIANCE_CODES.has(c.legalLanguageCode);

const isOpenFinancialItem = (item) => {
  if (!item) return false;
  if (item.reportType === 'dsraShortfall') return true;
  const pending = parseFloat(String(item.amountPending || '0').replace(/,/g, '')) || 0;
  return pending > 0;
};
const isLegalAccountWithDsra = (rows, dsraMonitoringAction, nfCompliances) => {
  if (isLegalAccount(rows)) return true;
  if (dsraMonitoringAction) {
    const lower = dsraMonitoringAction.toLowerCase().trim();
    if (LEGAL_MONITORING_ACTIONS.some((k) => lower.includes(k))) return true;
  }
  if (nfCompliances && nfCompliances.some((c) => {
    if (!isOpenNonCompliance(c)) return false;
    const a = (c.monitoringAction || '').toLowerCase().trim();
    return LEGAL_MONITORING_ACTIONS.some((k) => a.includes(k));
  })) return true;
  return false;
};

const getDpdColor = (dpd) => {
  if (dpd === '-' || dpd === 0) return 'var(--color-text-secondary)';
  if (dpd > 90) return 'var(--color-text-danger)';
  if (dpd > 30) return 'var(--color-text-warning)';
  return 'var(--color-text-info)';
};

const getDpdBg = (dpd) => {
  if (dpd === '-' || dpd === 0) return 'var(--color-background-secondary)';
  if (dpd > 90) return 'var(--color-background-danger)';
  if (dpd > 30) return 'var(--color-background-warning)';
  return 'var(--color-background-info)';
};

// ─── Type labels / colours ────────────────────────────────────────────────────

const TYPE_LABELS = {
  emi: 'EMI',
  legalCharge: 'Legal charge',
  penalCharge: 'Penal charge',
  dsraShortfall: 'DSRA shortfall',
  nachFailure: 'NACH failure',
  chequeBounce: 'Cheque bounce',
  nonFinancial: 'Non-financial',
};

const TYPE_BG = {
  emi: 'var(--color-background-info)',
  legalCharge: 'var(--color-background-danger)',
  penalCharge: 'var(--color-background-warning)',
  dsraShortfall: 'var(--color-background-info)',
  nachFailure: 'var(--color-background-warning)',
  chequeBounce: 'var(--color-background-success)',
  nonFinancial: 'var(--color-background-warning)',
};

const TYPE_TEXT = {
  emi: 'var(--color-text-info)',
  legalCharge: 'var(--color-text-danger)',
  penalCharge: 'var(--color-text-warning)',
  dsraShortfall: 'var(--color-text-info)',
  nachFailure: 'var(--color-text-warning)',
  chequeBounce: 'var(--color-text-success)',
  nonFinancial: 'var(--color-text-warning)',
};

const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'emi', label: 'EMI' },
  { value: 'legalCharge', label: 'Legal charge' },
  { value: 'penalCharge', label: 'Penal charge' },
  { value: 'dsraShortfall', label: 'DSRA shortfall' },
  { value: 'nachFailure', label: 'NACH failure' },
  { value: 'chequeBounce', label: 'Cheque bounce' },
  { value: 'nonFinancial', label: 'Non-financial' },
];

// ─── Popover ──────────────────────────────────────────────────────────────────
// `accounts` can be either:
//   - an array of plain name strings (non-compliant / legal) → clicking
//     scrolls to that account's row via onAccountClick, OR
//   - an array of { accountId, accountName } objects (good standing) → these
//     accounts have no row on this page (no issues this month), so clicking
//     renders as a real navigation Link to the account instead.
const AccountNamesPopover = ({ accounts, onClose, title, colorVar, bgVar, onAccountClick }) => (
  <div className="mi-popover-overlay" onClick={onClose}>
    <div className="mi-popover-modal" onClick={(e) => e.stopPropagation()}>
      <div className="mi-popover-header" style={{ background: bgVar }}>
        <span className="mi-popover-header-title" style={{ color: colorVar }}>
          {title}
        </span>
        <button className="mi-popover-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="mi-popover-body">
        {accounts.length === 0 ? (
          <div className="mi-popover-empty">No accounts</div>
        ) : (
          accounts.map((entry, i) => {
            const isObj = typeof entry === 'object' && entry !== null;
            const name = isObj ? entry.accountName : entry;
            const accountId = isObj ? entry.accountId : null;

            return (
              <div key={(name || 'account') + i} className="mi-popover-item">
                <span className="mi-popover-badge" style={{ background: bgVar, color: colorVar }}>
                  {i + 1}
                </span>
                {accountId ? (
                  <Link
                    to={`/su/accounts/${accountId}`}
                    className="mi-popover-name mi-popover-name-link"
                    onClick={onClose}
                  >
                    {name}
                  </Link>
                ) : (
                  <button
                    className="mi-popover-name mi-popover-name-link"
                    onClick={() => { onAccountClick && onAccountClick(name); onClose(); }}
                    type="button"
                  >
                    {name}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
      <div className="mi-popover-footer">
        <button className="mi-popover-close-btn" style={{ background: colorVar }} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  </div>
);

// ─── Stats summary table ──────────────────────────────────────────────────────

const SummaryStatsTable = ({
  totalClients,
  goodStandingCount,
  nonCompliantAccounts,
  legalActionAccounts,
  onGoodStandingClick,
  onNonCompliantClick,
  onLegalClick,
  yearMonth,
}) => {
  const rows = [
    { serial: 'A', label: 'Total Active clients', value: totalClients, onClick: null },
    {
      serial: 'B',
      label: 'Good Standing clients',
      value: goodStandingCount,
      onClick: goodStandingCount > 0 ? onGoodStandingClick : null,
    },
    {
      serial: 'C',
      label: 'Clients with some Non-Compliance',
      value: nonCompliantAccounts.length,
      onClick: nonCompliantAccounts.length > 0 ? onNonCompliantClick : null,
    },
    {
      serial: 'D',
      label: 'Clients under Legal Action',
      value: legalActionAccounts.length,
      onClick: legalActionAccounts.length > 0 ? onLegalClick : null,
    },
  ];

  return (
    <div className="mi-stats-card">
      <div className="mi-stats-card-header">
        <span className="mi-stats-card-title">Summary</span>
        {yearMonth && (
          <span className="mi-stats-card-month">{yearMonth.monthName} {yearMonth.year}</span>
        )}
      </div>
      <div className="mi-stats-table-wrap">
        <table className="mi-stats-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }} />
              <th>Particulars</th>
              <th style={{ width: '120px' }}>Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td>
                  <span className="mi-stats-row-serial">{row.serial}</span>
                </td>
                <td>
                  <span className="mi-stats-row-label">{row.label}</span>
                </td>
                <td>
                  {row.onClick ? (
                    <button
                      className="mi-stats-count-link"
                      onClick={row.onClick}
                      type="button"
                    >
                      {row.value}
                    </button>
                  ) : (
                    <span className="mi-stats-count">{row.value}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Human-readable title for a non-financial compliance ─────────────────────
// Mirrors MonitoringIntelligenceUI.js exactly: prefer a genuine reportType
// (anything other than the literal placeholder "nonFinancial"), otherwise
// derive a readable label from the clause text. legalLanguageCode is always
// a raw snake_case key (e.g. "emi_default", "continuous_dsra_dip") and must
// never be shown to the user.
const prettifyCode = (code) =>
  code.split('_').filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');


const getComplianceDisplayTitle = (c) => {
  const rt = (c.reportType || '').trim();
  const looksLikeCode = /^[a-z0-9]+(_[a-z0-9]+)+$/.test(rt);
  if (rt && rt !== 'nonFinancial' && !looksLikeCode) return rt;
  const clause = c.clause || '';
  const m1 = clause.match(/Non[- ]furnishing of (.+)$/i);
  if (m1) return m1[1].trim();
  const m2 = clause.match(/ - ([^-]+)$/);
  if (m2) return m2[1].trim();
  return looksLikeCode ? prettifyCode(rt) : 'Non Finance';
};

// ─── Non-financial compliance names (+ DPD) for an account ───────────────────
// Only genuinely open items (see isOpenNonCompliance) are surfaced, so once
// a compliance is complied it disappears here exactly as it does on the
// MI dashboard. DPD is computed from the compliance's own dueDate.
const getNonFinancialNames = (nonFinancialComplianceData, accountName) => {
  const list = nonFinancialComplianceData && nonFinancialComplianceData[accountName]
    ? nonFinancialComplianceData[accountName]
    : [];
  const seenTitles = new Set();
  const result = [];
  for (const c of list) {
    if (isOpenNonCompliance(c)) {
      const label = getComplianceDisplayTitle(c);
      if (!label || !label.trim()) continue;
      if (!seenTitles.has(label)) {
        seenTitles.add(label);
        result.push({ title: label, lrn: c.lrn, dpd: calcDPD(c.dueDate) });
      }
    }
  }
  return result;
};

// ─── Non-financial compliance names (+ DPD) for a specific loan ──────────────
const getNonFinancialNamesForLoan = (nonFinancialComplianceData, accountName, lrn) => {
  const list = nonFinancialComplianceData && nonFinancialComplianceData[accountName]
    ? nonFinancialComplianceData[accountName]
    : [];
  const seenTitles = new Set();
  const result = [];
  for (const c of list) {
    if (c.lrn !== lrn) continue;
    if (isOpenNonCompliance(c)) {
      const label = getComplianceDisplayTitle(c);
      if (!label || !label.trim()) continue;
      if (!seenTitles.has(label)) {
        seenTitles.add(label);
        result.push({ title: label, dpd: calcDPD(c.dueDate) });
      }
    }
  }
  return result;
};

const getNonFinancialComments = (nonFinancialComplianceData, accountName) => {
  const list = nonFinancialComplianceData && nonFinancialComplianceData[accountName]
    ? nonFinancialComplianceData[accountName]
    : [];
  const seenLrns = new Set();
  const comments = [];
  for (const c of list) {
    if (!isOpenNonCompliance(c)) continue;
    if (seenLrns.has(c.lrn)) continue;
    seenLrns.add(c.lrn);
    if (c.monitoringComment && c.monitoringComment.trim()) {
      comments.push(c.monitoringComment.trim());
    }
  }
  return comments;
};

// ─── Accumulate all monitoring comments for an account ───────────────────────
const buildRecommendation = (rows, dsraMonitoringMap, nonFinancialComplianceData, accountName) => {
  const seen = new Set();
  const parts = [];

  const push = (comment) => {
    if (!comment) return;
    const trimmed = comment.trim();
    if (!trimmed) return;
    const lower = trimmed.toLowerCase();
    if (!seen.has(lower)) {
      seen.add(lower);
      parts.push(trimmed);
    }
  };

  if (dsraMonitoringMap && dsraMonitoringMap.monitoringComment) {
    push(dsraMonitoringMap.monitoringComment);
  }

  for (const row of rows) {
    if (row.item && row.item.monitoringComment) {
      push(row.item.monitoringComment);
    }
  }

  getNonFinancialComments(nonFinancialComplianceData, accountName).forEach(push);

  return parts.join(' • ');
};

// ─── Given an account/loan's full type + non-financial breakdown, return
// only what should be visible for the currently selected type filter. ────────
const getDisplayItems = (typesWithDpd, nfNames, filter) => {
  if (filter === 'nonFinancial') return { types: [], nfNames };
  if (filter === 'all') return { types: typesWithDpd, nfNames };
  return { types: typesWithDpd.filter((t) => t.type === filter), nfNames: [] };
};

// ─── MISummaryUI ──────────────────────────────────────────────────────────────

const MISummaryUI = ({
  context,
  errorList,
  rawList,
  accountGroups,
  accountSummaries,
  nonFinancialComplianceData,
  yearMonth,
  loading,
  activeAccounts,
  activeAccountsList,
  loanIdMap,
  miReportExists,
  dsraByAccountMap,
  accountMonitoringMap,
  onPreviousClick,
  onNextClick,
  onRefreshClick,
}) => {
  const [showLoans, setShowLoans] = useState(false);
  const [popover, setPopover] = useState(null);
  const [dpdSort, setDpdSort] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const accountRowRefs = React.useRef({});

  const { totalClients, goodStandingCount, goodStandingAccounts, nonCompliantAccounts, legalActionAccounts } =
    useMemo(() => {
      const total = typeof activeAccounts === 'number' && activeAccounts >= 0
        ? activeAccounts
        : Object.keys(accountGroups).length;

      const allNames = Object.keys(accountGroups)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      const legalList = [];
      const ncList = [];

      allNames.forEach((name) => {
        const rows = accountGroups[name] || [];
        const dsraMon = accountMonitoringMap && accountMonitoringMap[name]
          ? accountMonitoringMap[name].monitoringAction
          : null;
        const nfCompliances = nonFinancialComplianceData && nonFinancialComplianceData[name];
        if (isLegalAccountWithDsra(rows, dsraMon, nfCompliances)) {
          legalList.push(name);
        } else {
          ncList.push(name);
        }
      });

      // Good standing = active accounts (from activeAccountsList, sourced from
      // the API) that never showed up anywhere in accountSummaries — i.e. no
      // financial or non-financial issue at all this month.
      const usedAccountIds = new Set((rawList || []).map((a) => a.accountId).filter(Boolean));
      const goodList = (activeAccountsList || []).filter(
        (a) => a && a.accountId && !usedAccountIds.has(a.accountId)
      );

      return {
        totalClients: total,
        goodStandingCount: goodList.length,
        goodStandingAccounts: goodList,
        nonCompliantAccounts: ncList,
        legalActionAccounts: legalList,
      };
    }, [
      accountGroups,
      accountSummaries,
      activeAccounts,
      activeAccountsList,
      accountMonitoringMap,
      nonFinancialComplianceData,
    ]);

  const tableRows = useMemo(() => {
    const nfPenalByAccount = {};
    const nfPenalByLoan = {};
    (rawList || []).forEach((acc) => {
      (acc.loans || []).forEach((ln) => {
        const p = parseAmt(ln.nonFinancialPenalCharge);
        if (p <= 0) return;
        nfPenalByAccount[acc.accountName] = (nfPenalByAccount[acc.accountName] || 0) + p;
        const key = acc.accountName + '||' + ln.lrn;
        nfPenalByLoan[key] = (nfPenalByLoan[key] || 0) + p;
      });
    });

    return Object.entries(accountSummaries)
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map(([accountName, summary]) => {
        const rows = accountGroups[accountName] || [];

        const amountPending = summary.totalAmountPending || 0;

        const dsraList = dsraByAccountMap && dsraByAccountMap[accountName]
          ? dsraByAccountMap[accountName]
          : [];
        let dsraDip = 0;
        dsraList.forEach((d) => {
          const val = parseFloat((d.dsraDip || '0').replace(/,/g, '')) || 0;
          dsraDip += val;
        });
        const nfPenal = nfPenalByAccount[accountName] || 0;

        const dsraMon = accountMonitoringMap && accountMonitoringMap[accountName]
          ? accountMonitoringMap[accountName]
          : null;
        const recommendation = buildRecommendation(rows, dsraMon, nonFinancialComplianceData, accountName);
        // Overall worst DPD across the account — still used for the column sort.
        let dpd = '-';
        let worstDpd = -1;
        rows.forEach((row) => {
          const d = calcDPD(getDpdDateForItem(row.item));
          if (typeof d === 'number' && d > worstDpd) {
            worstDpd = d;
            dpd = d;
          }
        });

        const types = [
          ...new Set(
            rows
              .filter((r) => isOpenFinancialItem(r.item))
              .map((r) => (r.item && r.item.reportType ? r.item.reportType : null))
              .filter(Boolean)
              .filter((t) => t !== 'dsraShortfall' && t !== 'nonFinancialPlaceholder')
          ),
        ];

        const hasDsra = rows.some((r) => r.item && r.item.reportType === 'dsraShortfall');
        if (hasDsra && !types.includes('dsraShortfall')) types.push('dsraShortfall');

        // Per-type DPD: EMI/legal/penal/DSRA all use their own record's
        // date of default (same field the MI dashboard uses).
        const typesWithDpd = types.map((t) => {
          let tDpd = '-';
          let worstT = -1;
          rows
            .filter((r) => r.item && r.item.reportType === t && isOpenFinancialItem(r.item))
            .forEach((r) => {
              const d = calcDPD(getDpdDateForItem(r.item));
              if (typeof d === 'number' && d > worstT) {
                worstT = d;
                tDpd = d;
              }
            });
          return { type: t, dpd: tDpd };
        });

        const nfNames = getNonFinancialNames(nonFinancialComplianceData, accountName);
        const hasNonFinancialBadge = nfNames.length > 0;

        const loanBreakup = Object.entries(summary.loans || {}).map(([lrn, loanData]) => {
          const loanRows = rows.filter((r) => r.item && r.item.lrn === lrn);

          const loanTypes = [
            ...new Set(
              loanRows
                .filter((r) => isOpenFinancialItem(r.item))
                .map((r) => r.item.reportType)
                .filter(Boolean)
                .filter((t) => t !== 'dsraShortfall' && t !== 'nonFinancialPlaceholder')
            ),
          ];

          const loanTypesWithDpd = loanTypes.map((t) => {
            let tDpd = '-';
            let worstT = -1;
            loanRows
              .filter((r) => r.item.reportType === t && isOpenFinancialItem(r.item))
              .forEach((r) => {
                const d = calcDPD(getDpdDateForItem(r.item));
                if (typeof d === 'number' && d > worstT) {
                  worstT = d;
                  tDpd = d;
                }
              });
            return { type: t, dpd: tDpd };
          });

          const loanNfNames = getNonFinancialNamesForLoan(
            nonFinancialComplianceData, accountName, lrn
          );

          return {
            lrn,
            loanId: loanData.loanId,
            amountPending: loanData.totalPending || 0,
            nfPenal: nfPenalByLoan[accountName + '||' + lrn] || 0,
            typesWithDpd: loanTypesWithDpd,
            nfNames: loanNfNames,
          };
        });

        const anyLoanId = (() => {
          const loans = summary.loans || {};
          for (const lrn of Object.keys(loans)) {
            if (loans[lrn] && loans[lrn].loanId) return loans[lrn].loanId;
          }
          return (loanIdMap && loanIdMap[accountName]) ? loanIdMap[accountName] : null;
        })();

        const dsraMon2 = accountMonitoringMap && accountMonitoringMap[accountName];
        const nfCompliances2 = nonFinancialComplianceData && nonFinancialComplianceData[accountName];
        const isLegal = isLegalAccountWithDsra(
          rows,
          dsraMon2 ? dsraMon2.monitoringAction : null,
          nfCompliances2
        );

        return {
          accountName,
          summary,
          amountPending,
          dsraDip,
          nfPenal,
          dpd,
          types,
          typesWithDpd,
          hasNonFinancialBadge,
          nfNames,
          loanBreakup,
          anyLoanId,
          recommendation,
          isLegal,
        };
      });
  }, [
    accountGroups,
    accountSummaries,
    nonFinancialComplianceData,
    loanIdMap,
    dsraByAccountMap,
    accountMonitoringMap,
    rawList,
  ]);

  const filteredRows = useMemo(() => {
    if (typeFilter === 'all') return tableRows;
    return tableRows.filter((row) => {
      if (typeFilter === 'nonFinancial') return row.hasNonFinancialBadge;
      return row.types.includes(typeFilter);
    });
  }, [tableRows, typeFilter]);

  const sortedRows = useMemo(() => {
    if (!dpdSort) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const av = a.dpd === '-' ? -1 : a.dpd;
      const bv = b.dpd === '-' ? -1 : b.dpd;
      return dpdSort === 'asc' ? av - bv : bv - av;
    });
  }, [filteredRows, dpdSort]);

  const nonCompliantRows = sortedRows.filter((r) => !r.isLegal);
  const legalRows = sortedRows.filter((r) => r.isLegal);

  const scrollToAccount = (accountName) => {
    const el = accountRowRefs.current[accountName];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('mi-row-highlight');
      setTimeout(() => el.classList.remove('mi-row-highlight'), 2000);
    }
  };

  const COL_COUNT = 8;

  const thStyle = {
    padding: '10px 14px',
    textAlign: 'left',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    fontSize: '0.72rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    background: 'var(--color-background-secondary)',
    borderBottom: '2px solid var(--color-border-secondary)',
    borderRight: '1px solid var(--color-border-tertiary)',
    position: 'sticky',
    top: 0,
    zIndex: 4,
  };

  const thStyleLast = { ...thStyle, borderRight: 'none' };

  const isLoading = loading || (!rawList && !errorList);

  const renderTableHead = () => (
    <thead>
      <tr>
        <th style={thStyle}>S.no.</th>
        <th style={thStyle}>Account</th>
        <th style={thStyle}>Non-Compliances</th>
        <th style={thStyle}>DSRA Dip (₹)</th>
        <th style={thStyle}>Penal Charge (₹)</th>
        <th style={thStyle}>Amount Overdue (₹)</th>
        <th style={thStyle}>Recommendation</th>
        <th
          style={{ ...thStyleLast, cursor: 'pointer', userSelect: 'none' }}
          onClick={() =>
            setDpdSort((s) => (s === 'asc' ? 'desc' : s === 'desc' ? null : 'asc'))
          }
        >
          <span className="mi-th-sort">
            DPD (days)
            <span className="mi-sort-icon">
              {dpdSort === 'asc' ? ' ↑' : dpdSort === 'desc' ? ' ↓' : ' ↕'}
            </span>
          </span>
        </th>
      </tr>
    </thead>
  );

  const renderTableFoot = (rows) => {
    if (rows.length === 0) return null;
    const tPnd = rows.reduce((s, r) => s + r.amountPending, 0);
    const tDsra = rows.reduce((s, r) => s + r.dsraDip, 0);
    const tNfPenal = rows.reduce((s, r) => s + r.nfPenal, 0);
    return (
      <tfoot>
        <tr className="mi-tfoot-row">
          <td colSpan={3} className="mi-tfoot-label">Total</td>
          <td className="mi-tfoot-value">{fmt(tDsra)}</td>
          <td className="mi-tfoot-value">{tNfPenal > 0 ? fmt(tNfPenal) : '—'}</td>
          <td className="mi-tfoot-value" style={{ color: 'var(--color-text-danger)' }}>
            {fmt(tPnd)}
          </td>
          <td className="mi-tfoot-value" style={{ color: 'var(--color-text-secondary)' }}>—</td>
          <td className="mi-tfoot-value" style={{ color: 'var(--color-text-secondary)' }}>—</td>
        </tr>
      </tfoot>
    );
  };

  const renderAccountRow = (row, idx, globalSerial) => {
    const display = getDisplayItems(row.typesWithDpd, row.nfNames, typeFilter);

    return (
      <React.Fragment key={row.accountName}>
        <tr
          ref={(el) => { accountRowRefs.current[row.accountName] = el; }}
          className={`mi-account-row${idx % 2 === 0 ? ' mi-row-even' : ' mi-row-odd'}${showLoans && row.loanBreakup.length > 0 ? ' mi-row-no-border' : ''
            }`}
        >
          <td className="mi-td mi-td-serial">{globalSerial}</td>

          <td className="mi-td">
            {row.anyLoanId ? (
              <Link to="/su/monitoringIntelligence" className="mi-account-link">
                {row.accountName}
              </Link>
            ) : (
              <span className="mi-account-name">{row.accountName}</span>
            )}
          </td>

          <td className="mi-td" style={{ minWidth: 220 }}>
            <div className="mi-nc-list">
              {display.types.map((t, ti) => (
                <div key={t.type} className="mi-nc-item">
                  <span className="mi-nc-num">{ti + 1}.</span>
                  <span
                    className="mi-type-badge"
                    style={{
                      background: TYPE_BG[t.type] || 'var(--color-background-secondary)',
                      color: TYPE_TEXT[t.type] || 'var(--color-text-secondary)',
                    }}
                  >
                    {TYPE_LABELS[t.type] || t.type}
                  </span>
                </div>
              ))}
              {display.nfNames.map((nf, nfi) => (
                <div key={'nf-' + nfi} className="mi-nc-item">
                  <span className="mi-nc-num">{display.types.length + nfi + 1}.</span>
                  <span
                    className="mi-type-badge"
                    style={{
                      background: 'var(--color-background-warning)',
                      color: 'var(--color-text-warning)',
                      justifyContent: 'flex-start',
                      minWidth: 0,
                    }}
                  >
                    {nf.title}
                  </span>
                </div>
              ))}
              {display.types.length === 0 && display.nfNames.length === 0 && (
                <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
              )}
            </div>
          </td>

          <td className="mi-td mi-td-amount">
            {row.dsraDip > 0 ? fmt(row.dsraDip) : '—'}
          </td>
          <td className="mi-td mi-td-amount">
            {row.nfPenal > 0 ? fmt(row.nfPenal) : '—'}
          </td>

          <td
            className="mi-td"
            style={{
              color: row.amountPending > 0
                ? 'var(--color-text-danger)'
                : 'var(--color-text-success)',
              fontWeight: 600,
            }}
          >
            {fmt(row.amountPending)}
          </td>

          <td
            className="mi-td"
            style={{ maxWidth: 240, fontSize: '0.8rem', color: 'var(--color-text-primary)' }}
          >
            {row.recommendation || '—'}
          </td>

          <td className="mi-td">
            <div className="mi-nc-list">
              {display.types.map((t, ti) => (
                <div key={'dpd-' + t.type} className="mi-nc-item">
                  <span className="mi-nc-num">{ti + 1}.</span>
                  <span
                    className="mi-dpd-badge"
                    style={{ background: getDpdBg(t.dpd), color: getDpdColor(t.dpd) }}
                  >
                    {t.dpd === '-' ? '—' : t.dpd + 'd'}
                  </span>
                </div>
              ))}
              {display.nfNames.map((nf, nfi) => (
                <div key={'dpd-nf-' + nfi} className="mi-nc-item">
                  <span className="mi-nc-num">{display.types.length + nfi + 1}.</span>
                  <span
                    className="mi-dpd-badge"
                    style={{ background: getDpdBg(nf.dpd), color: getDpdColor(nf.dpd) }}
                  >
                    {nf.dpd === '-' ? '—' : nf.dpd + 'd'}
                  </span>
                </div>
              ))}
              {display.types.length === 0 && display.nfNames.length === 0 && (
                <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
              )}
            </div>
          </td>
        </tr>

        {showLoans &&
          row.loanBreakup.map((loan, li) => {
            const loanDisplay = getDisplayItems(loan.typesWithDpd, loan.nfNames, typeFilter);
            return (
              <tr
                key={row.accountName + '-loan-' + loan.lrn + '-' + li}
                className={`mi-loan-row${li === row.loanBreakup.length - 1 ? ' mi-loan-row-last' : ''
                  }`}
              >
                <td className="mi-td mi-td-loan-serial">{globalSerial}.{li + 1}</td>
                <td className="mi-td mi-td-loan-name">
                  <span className="mi-loan-dot" />
                  {loan.lrn || 'N/A'}
                </td>
                <td className="mi-td" style={{ minWidth: 220 }}>
                  <div className="mi-nc-list">
                    {loanDisplay.types.map((t, ti) => (
                      <div key={t.type} className="mi-nc-item">
                        <span className="mi-nc-num">{ti + 1}.</span>
                        <span
                          className="mi-type-badge"
                          style={{
                            background: TYPE_BG[t.type] || 'var(--color-background-secondary)',
                            color: TYPE_TEXT[t.type] || 'var(--color-text-secondary)',
                            fontSize: '0.68rem',
                          }}
                        >
                          {TYPE_LABELS[t.type] || t.type}
                        </span>
                      </div>
                    ))}
                    {loanDisplay.nfNames.map((nf, nfi) => (
                      <div key={'lnf-' + nfi} className="mi-nc-item">
                        <span className="mi-nc-num">{loanDisplay.types.length + nfi + 1}.</span>
                        <span
                          className="mi-type-badge"
                          style={{
                            background: 'var(--color-background-warning)',
                            color: 'var(--color-text-warning)',
                            fontSize: '0.68rem',
                            justifyContent: 'flex-start',
                            minWidth: 0,
                          }}
                        >
                          {nf.title}
                        </span>
                      </div>
                    ))}
                    {loanDisplay.types.length === 0 && loanDisplay.nfNames.length === 0 && (
                      <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
                    )}
                  </div>
                </td>
                <td className="mi-td mi-td-loan-amount">—</td>
                <td className="mi-td mi-td-loan-amount">
                  {loan.nfPenal > 0 ? fmt(loan.nfPenal) : '—'}
                </td>
                <td
                  className="mi-td"
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: loan.amountPending > 0
                      ? 'var(--color-text-danger)'
                      : 'var(--color-text-success)',
                  }}
                >
                  {fmt(loan.amountPending)}
                </td>
                <td className="mi-td">—</td>
                <td className="mi-td">
                  <div className="mi-nc-list">
                    {loanDisplay.types.map((t, ti) => (
                      <div key={'ldpd-' + t.type} className="mi-nc-item">
                        <span className="mi-nc-num">{ti + 1}.</span>
                        <span style={{ fontSize: '0.8rem', color: getDpdColor(t.dpd), fontWeight: 600 }}>
                          {t.dpd === '-' ? '—' : t.dpd + 'd'}
                        </span>
                      </div>
                    ))}
                    {loanDisplay.nfNames.map((nf, nfi) => (
                      <div key={'ldpd-nf-' + nfi} className="mi-nc-item">
                        <span className="mi-nc-num">{loanDisplay.types.length + nfi + 1}.</span>
                        <span style={{ fontSize: '0.8rem', color: getDpdColor(nf.dpd), fontWeight: 600 }}>
                          {nf.dpd === '-' ? '—' : nf.dpd + 'd'}
                        </span>
                      </div>
                    ))}
                    {loanDisplay.types.length === 0 && loanDisplay.nfNames.length === 0 && (
                      <span style={{ color: 'var(--color-text-secondary)' }}>—</span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
      </React.Fragment>
    );
  };

  const renderToolbar = (title) => (
    <div className="mi-table-toolbar mi-table-toolbar-sticky">
      <div className="mi-toolbar-left">
        <span className="mi-table-title">{title}</span>
      </div>
      <div className="mi-toolbar-right">
        <div className="mi-filter-group">
          <label className="mi-filter-label">Type</label>
          <select
            className="mi-filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {TYPE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="mi-toggle-row">
          <span className="mi-toggle-label">Show loans</span>
          <button
            type="button"
            role="switch"
            aria-checked={showLoans}
            className={`mi-toggle-track${showLoans ? ' mi-toggle-track--on' : ''}`}
            onClick={() => setShowLoans((v) => !v)}
          >
            <span className="mi-toggle-thumb" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderEmptyRow = () => (
    <tr>
      <td
        colSpan={COL_COUNT}
        style={{
          padding: '32px',
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
        }}
      >
        No data available
      </td>
    </tr>
  );

  return (
    <SuperUserHomePageUI
      context={context}
      className="pg_su_mi"
      topnav="monitoringIntelligence"
      page="monitoringIntelligence_summary"
      centered={isLoading}
      dimension="w1600"
    >
      {isLoading && <SpinnerComponentUI />}

      {!isLoading && (
        <div>
          <LRTableActionsUI
            location="top"
            left={
              <span className="g_flex_left g_flex_h">
                <IconButtonUI icon="icon-refresh" onClick={onRefreshClick} label="Refresh" />
                <span className="g_m_left_100 g_large">
                  Monitoring Intelligence — Summary
                  {yearMonth ? ', ' + yearMonth.monthName + ' ' + yearMonth.year : ''}
                </span>
              </span>
            }
            right={
              <span className="g_flex_h g_m_right_100">
                <Link to="#" onClick={onPreviousClick}>Previous</Link>
                <span className="g_m_left_100" />
                <Link to="#" onClick={onNextClick}>Next</Link>
                <span className="g_m_left_100" />
                <MIDownloadButtonUI yearMonth={yearMonth} context={context} />
                <span className="g_m_left_100" />
              </span>
            }
          />

          <MonitoringIntelligencePageNavUI
            context={context}
            page="monitoringIntelligence_summary"
          />
          <MIReportApproval
            yearMonth={yearMonth}
            context={context}
            reportExists={miReportExists}
          />

          <div style={{ paddingBottom: '40px', marginTop: '16px' }}>

            <SummaryStatsTable
              totalClients={totalClients}
              goodStandingCount={goodStandingCount}
              nonCompliantAccounts={nonCompliantAccounts}
              legalActionAccounts={legalActionAccounts}
              yearMonth={yearMonth}
              onGoodStandingClick={() =>
                setPopover({ type: 'goodStanding', accounts: goodStandingAccounts })
              }
              onNonCompliantClick={() =>
                setPopover({ type: 'nonCompliant', accounts: nonCompliantAccounts })
              }
              onLegalClick={() =>
                setPopover({ type: 'legal', accounts: legalActionAccounts })
              }
            />

            {/* ── Card 1: Clients with Non-Compliances ── */}
            <CardUI className="scard g_m_top_100 mi-section-card" style={{ padding: 0, overflow: 'visible' }}>
              {renderToolbar(
                'Clients with Non-Compliances' +
                (yearMonth ? ' — ' + yearMonth.monthName + ' ' + yearMonth.year : '')
              )}
              <div className="mi-table-scroll-wrap">
                <table className="mi-summary-table">
                  {renderTableHead()}
                  <tbody>
                    {nonCompliantRows.length === 0
                      ? renderEmptyRow()
                      : nonCompliantRows.map((row, idx) =>
                        renderAccountRow(row, idx, idx + 1)
                      )}
                  </tbody>
                  {renderTableFoot(nonCompliantRows)}
                </table>
              </div>
            </CardUI>

            {/* ── Card 2: Clients under Legal Action ── */}
            {legalRows.length > 0 && (
              <CardUI className="scard g_m_top_100 mi-section-card" style={{ padding: 0, overflow: 'visible' }}>
                {renderToolbar(
                  'Clients under Legal Action' +
                  (yearMonth ? ' — ' + yearMonth.monthName + ' ' + yearMonth.year : '')
                )}
                <div className="mi-table-scroll-wrap">
                  <table className="mi-summary-table">
                    {renderTableHead()}
                    <tbody>
                      {legalRows.map((row, idx) =>
                        renderAccountRow(row, idx, idx + 1)
                      )}
                    </tbody>
                    {renderTableFoot(legalRows)}
                  </table>
                </div>
              </CardUI>
            )}
          </div>
        </div>
      )}

      {popover && popover.type === 'goodStanding' && (
        <AccountNamesPopover
          accounts={popover.accounts}
          title={'Good standing clients (' + popover.accounts.length + ')'}
          colorVar="var(--color-text-success)"
          bgVar="var(--color-background-success)"
          onClose={() => setPopover(null)}
        />
      )}
      {popover && popover.type === 'nonCompliant' && (
        <AccountNamesPopover
          accounts={popover.accounts}
          title={'Clients with non-compliance (' + popover.accounts.length + ')'}
          colorVar="var(--color-text-warning)"
          bgVar="var(--color-background-warning)"
          onClose={() => setPopover(null)}
          onAccountClick={scrollToAccount}
        />
      )}
      {popover && popover.type === 'legal' && (
        <AccountNamesPopover
          accounts={popover.accounts}
          title={'Clients with legal action (' + popover.accounts.length + ')'}
          colorVar="var(--color-text-danger)"
          bgVar="var(--color-background-danger)"
          onClose={() => setPopover(null)}
          onAccountClick={scrollToAccount}
        />
      )}
    </SuperUserHomePageUI>
  );
};

export default MISummaryUI;